import React, { useEffect, useRef, useState } from 'react'
import Pagination from "../../components/pagination/pagination";
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../components/button/button';
import { useDispatch } from 'react-redux';
import errorToast from '../../components/toasts/errorToast';
import moment from 'moment';
import { systemLogsList } from '../../redux/actions/logsAction';
import ReactSelectFiled from '../../components/forms/reactSelect';
import { Modal } from 'react-bootstrap';
import { logsExportApi } from '../../redux/apis/logsApis';
import { useAuth } from '../../context/AuthContext';

const STATUS_CLASS = {
    SUCCESS: "badge bg-success",
    FAILED: "badge bg-danger",
    PENDING: "badge bg-warning text-dark",
};

const EMPTY_FILTERS = {
    event_category: "",
    event_action: "",
    event_status: "",
    from_date: "",
    to_date: "",
};
const EMPTY_EXPORT_FORM = { from_date: "", to_date: "" };

const CATEGORY_STYLES = {
    AUTH: { backgroundColor: "#cfe2ff", color: "#084298" },
    USER: { backgroundColor: "#cff4fc", color: "#055160" },
    TEAM: { backgroundColor: "#e0f0ff", color: "#0c4a6e" },
    DOCUMENT: { backgroundColor: "#dbeafe", color: "#1e3a5f" },
    TRANSLATION: { backgroundColor: "#d1e7dd", color: "#0a3622" },
    RESOURCE: { backgroundColor: "#d2f4ea", color: "#13795b" },
    COMPANY: { backgroundColor: "#ffe5d0", color: "#6d2a05" },
    PUBSUB: { backgroundColor: "#f7d6e6", color: "#6a1a39" },
    CRON: { backgroundColor: "#fff3cd", color: "#664d03" },
};

const ACTIONS_BY_CATEGORY = {
    AUTH: ['LOGIN_SUCCESS', 'LOGIN_FAILED', 'FORGOT_PASSWORD', 'PASSWORD_RESET', 'TOKEN_VERIFIED'],
    USER: ['USER_CREATED', 'USER_EDITED', 'USER_DELETED', 'USER_STATUS_CHANGED'],
    TEAM: ['TEAM_CREATED', 'TEAM_EDITED', 'TEAM_DELETED', 'TEAM_USER_ADDED', 'TEAM_USER_REMOVED'],
    DOCUMENT: ['DOCUMENT_UPLOADED', 'DOCUMENT_CREATED', 'DOCUMENT_ASSIGNED', 'DOCUMENT_DELETED', 'FOLDER_CREATED', 'FOLDER_EDITED', 'FOLDER_DELETED'],
    TRANSLATION: ['DOCUMENT_TRANSLATED', 'BATCH_TRANSLATED', 'TRANSLATION_PUBLISHED', 'POST_EDITING_ASSIGNED', 'POST_EDIT_SAVED', 'POST_EDIT_SUBMITTED'],
    RESOURCE: ['GLOSSARY_CSV_UPLOADED', 'GLOSSARY_CREATED', 'GLOSSARY_DELETED'],
    COMPANY: ['COMPANY_REGISTERED'],
    PUBSUB: ['PUBSUB_RECEIVED', 'ENTITLEMENT_ACTIVE', 'ENTITLEMENT_RENEWED', 'ENTITLEMENT_CANCELLED', 'ENTITLEMENT_DELETED', 'ENTITLEMENT_PLAN_CHANGED', 'ENTITLEMENT_PLAN_CHANGE_CANCELLED', 'ENTITLEMENT_OFFER_ENDED', 'ACCOUNT_DELETED'],
    CRON: ['CRON_USAGE_REPORTED', 'CRON_STATUS_UPDATED', 'CRON_TM_CREATED'],
};

// const CATEGORY_OPTIONS = Object.keys(ACTIONS_BY_CATEGORY).map((c) => ({ label: c, value: c }));

const formatAction = (str) =>
    str.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

const CategoryBadge = ({ category }) => {
    const style = CATEGORY_STYLES[category] || { backgroundColor: "#e2e3e5", color: "#41464b" };
    return <span className="category-badge" style={style}>{category}</span>;
};

const StatusBadge = ({ status }) => (
    <span className={STATUS_CLASS[status] || "badge bg-secondary"}>{status}</span>
);

export const Logs = () => {
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [paginationData, setPaginationData] = useState({});

    const [filterForm, setFilterForm] = useState({ ...EMPTY_FILTERS });
    const [appliedFilters, setAppliedFilters] = useState({});
    const [searchInput, setSearchInput] = useState("");
    const searchMounted = useRef(false);

    const [selectedLog, setSelectedLog] = useState(null);
    const [showModal, setShowModal] = useState(false);

    /* Export modal */
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportForm, setExportForm] = useState({ ...EMPTY_EXPORT_FORM });
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { isAdmin } = useAuth();

    const onSuccess = (res) => {
        const { rows, pagination } = res.data.data;
        setLogs(rows);
        setPaginationData(pagination);
        setLoading(false);
    };

    const onError = (res) => {
        errorToast(res?.data?.message || "Failed to load logs");
        setLoading(false);
    };

    const buildParams = (filters) => {
        const params = { page, page_size: pageSize };
        Object.entries(filters).forEach(([k, v]) => {
            if (v) params[k] = v;
        });
        return params;
    };

    useEffect(() => {
        if (!filtersApplied) return;
        const params = { page, page_size: pageSize };
        Object.entries(appliedFilters).forEach(([k, v]) => { if (v) params[k] = v; });
        setLoading(true);
        dispatch(systemLogsList(params, onSuccess, onError));
    }, [page, pageSize, appliedFilters, filtersApplied, dispatch]);

    useEffect(() => {
        if (!searchMounted.current) {
            searchMounted.current = true;
            return;
        }
        const timer = setTimeout(() => {
            setPage(1);
            setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
            setFiltersApplied((prev) => searchInput ? true : prev);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleGetAllRecords = () => {
        setLoading(true);
        setFiltersApplied(true);
        dispatch(systemLogsList(buildParams(appliedFilters), onSuccess, onError));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterForm((prev) => ({
            ...prev,
            [name]: value,
            ...(name === "event_category" ? { event_action: "" } : {}),
        }));
    };

    const handleApplyFilters = () => {
        setPage(1);
        setAppliedFilters({ ...filterForm });
        setFiltersApplied(true);
    };

    const handleClearFilters = () => {
        setFilterForm({ ...EMPTY_FILTERS });
        setSearchInput("");
        setPage(1);
        setAppliedFilters({});
        setFiltersApplied(false);
    };

    // const openDetail = (log) => {
    //     setSelectedLog(log);
    //     setShowModal(true);
    // };

    /* ── Access guard ── */
    if (!isAdmin) {
        return (
            <div className="w-100 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
                <i className="bi bi-shield-lock fs_40 text-danger mb-3"></i>
                <h5 className="fw_600">Access Denied</h5>
                <p className="text-muted fs_14">This page is only accessible to company administrators.</p>
            </div>
        );
    }

    const renderMetadata = (metadata) => {
        if (!metadata) return <span className="text-muted">—</span>;

        if (metadata.changes && Object.keys(metadata.changes).length > 0) {
            return (
                <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Field</th>
                            <th>From</th>
                            <th>To</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(metadata.changes).map(([field, val]) => (
                            <tr key={field}>
                                <td><code>{field}</code></td>
                                <td><code>{val && val.from !== undefined ? JSON.stringify(val.from) : "—"}</code></td>
                                <td><code>{val && val.to !== undefined ? JSON.stringify(val.to) : "—"}</code></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        return (
            <pre className="bg-light p-2 rounded fs_12 mb-0" style={{ maxHeight: "200px", overflowY: "auto" }}>
                {JSON.stringify(metadata, null, 2)}
            </pre>
        );
    };

    /* Export uses the modal's date range — independent of the filter bar */
    const handleExportSubmit = async () => {
        if (!exportForm.from_date || !exportForm.to_date) {
            errorToast("Please set both From Date and To Date");
            return;
        }
        setExportLoading(true);
        try {
            const res = await logsExportApi({ from_date: exportForm.from_date, to_date: exportForm.to_date });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `system-logs-${exportForm.from_date}-to-${exportForm.to_date}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setShowExportModal(false);
            setExportForm({ ...EMPTY_EXPORT_FORM });
        } catch (error) {
            let message = 'Export failed';
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    message = json.message || message;
                } catch (_) { /* blob was not JSON */ }
            } else {
                message = error.response?.data?.message || message;
            }
            errorToast(message);
        } finally {
            setExportLoading(false);
        }
    };

    // const actionOptions = filterForm.event_category
    //     ? (ACTIONS_BY_CATEGORY[filterForm.event_category] || []).map((a) => ({ label: formatAction(a), value: a }))
    //     : Object.values(ACTIONS_BY_CATEGORY).flat().map((a) => ({ label: formatAction(a), value: a }));

    const colCount = isAdmin ? 7 : 6;

    return (
        <div className="d-flex vh-100">
            <div className="w-100 overflow-auto">
                <div className="w-100 z-1 header align-items-center border-bottom d-flex position-sticky top-0 bg-white py-2 justify-content-between">
                    <h2 className="fs_20 fw_400 fn_Roboto text-start px-3 pt-1">Activity Logs</h2>
                    {isAdmin &&
                        <CustomButton className='cursor_pointer fs_18 pb-1 mx-3 d-flex gap-2 align-items-center justify-content-center' style={{ height: '35px', width: '150px' }} variant='primary' onClick={() => setShowExportModal(true)}>
                            <i className="bi bi-download" />
                            Export CSV
                        </CustomButton>
                    }
                </div>

                <div className='d-flex flex-wrap gap-3 p-3'>

                    {/* Category */}
                    {/* <div className='text-start' style={{ width: '14rem' }}>
                        <ReactSelectFiled
                            onChange={(value) => handleFilterChange({ target: { name: "event_category", value: value || "" } })}
                            options={CATEGORY_OPTIONS}
                            label={"Category"}
                            value={filterForm.event_category}
                            placeholder={"Select category"}
                            isClearable
                        />
                    </div> */}

                    {/* Action — filtered by selected category */}
                    {/* <div className='text-start' style={{ width: '14rem' }}>
                        <ReactSelectFiled
                            onChange={(value) => handleFilterChange({ target: { name: "event_action", value: value || "" } })}
                            options={actionOptions}
                            label={"Action"}
                            value={filterForm.event_action}
                            placeholder={"Select action"}
                            isClearable
                        />
                    </div> */}

                    {/* Search */}
                    <div className='text-start'>
                        <span>Search</span>
                        <div style={{ width: '14rem' }}>
                            <input
                                type='text'
                                className='input-field w-100'
                                placeholder="Search logs..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Status */}
                    <div className='text-start' style={{ width: '14rem' }}>
                        <ReactSelectFiled
                            onChange={(value) => handleFilterChange({ target: { name: "event_status", value: value || "" } })}
                            options={[
                                { label: "SUCCESS", value: "SUCCESS" },
                                { label: "FAILED", value: "FAILED" },
                                { label: "PENDING", value: "PENDING" },
                            ]}
                            label={"Status"}
                            value={filterForm.event_status}
                            placeholder={"Select status"}
                            isClearable
                        />
                    </div>

                    {/* From Date */}
                    <div className='text-start'>
                        <span className="">From Date</span>
                        <div style={{ width: '14rem' }}>
                            <input
                                type='date'
                                className='input-field w-100'
                                name="from_date"
                                value={filterForm.from_date}
                                onChange={handleFilterChange}
                            />
                        </div>
                    </div>

                    {/* To Date */}
                    <div className='text-start'>
                        <span className="">To Date</span>
                        <div style={{ width: '14rem' }}>
                            <input
                                type='date'
                                className='input-field w-100'
                                name="to_date"
                                value={filterForm.to_date}
                                onChange={handleFilterChange}
                            />
                        </div>
                    </div>
                </div>

                <div className="d-flex flex-wrap justify-content-start gap-3 mx-3 pt-1 text-nowrap">
                    <CustomButton
                        variant="primary"
                        onClick={handleApplyFilters}
                        disabled={!filterForm.event_category && !filterForm.event_action && !filterForm.event_status && !filterForm.from_date && !filterForm.to_date}
                    >
                        Apply Filters
                    </CustomButton>
                    <CustomButton variant="primary" onClick={handleGetAllRecords}>
                        Show All
                    </CustomButton>
                    <CustomButton variant="secondary" onClick={handleClearFilters}>
                        Clear Filters
                    </CustomButton>
                </div>

                {filtersApplied && (
                    <>
                        <div className="m-3">
                            <div className="table-responsive overflow-auto" style={{ minHeight: '26rem', minWidth: '100%' }}>
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr className="text-start text-nowrap">
                                            <th width={'18%'} className='fw_400'>Timestamp</th>
                                            <th width={'12%'} className='fw_400'>Category</th>
                                            <th width={'18%'} className='fw_400'>Action</th>
                                            <th width={'10%'} className='fw_400'>Status</th>
                                            <th width={'18%'} className='fw_400'>Actor</th>
                                            <th width={'18%'} className='fw_400'>Target</th>
                                            {isAdmin && <th className='fw_400'>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={colCount}>
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : logs.length === 0 ? (
                                            <tr>
                                                <td colSpan={colCount} className="text-center text-muted py-5 fs_14">
                                                    No logs found
                                                </td>
                                            </tr>
                                        ) : (
                                            logs?.map((log, index) => (
                                                <tr key={index} className='text-start'>
                                                    <td className="text-nowrap">
                                                        {moment(log.createdAt).format("MMM D, YYYY h:mm A")}
                                                    </td>
                                                    <td>
                                                        <CategoryBadge category={log.event_category} />
                                                    </td>
                                                    <td>{formatAction(log.event_action)}</td>
                                                    <td>
                                                        <StatusBadge status={log.event_status} />
                                                    </td>
                                                    <td>{log.actor_email || log.actor_type || "—"}</td>
                                                    <td className="text-truncate" style={{ maxWidth: 0 }} title={log.target_type ? `${log.target_type}: ${log.target_id || "—"}` : "—"}>
                                                        {log.target_type
                                                            ? `${log.target_type}: ${log.target_id || "—"}`
                                                            : "—"}
                                                    </td>
                                                    {isAdmin &&
                                                        <td className='text-center'>
                                                            <span
                                                                className="text-primary cursor_pointer fs_13 fw_500"
                                                                // onClick={() => openDetail(log)}
                                                                onClick={() => navigate(`/logs/${log.id}`)}
                                                            >
                                                                View
                                                            </span>
                                                        </td>
                                                    }
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="mx-4 pb-3 d-flex justify-content-end">
                            {!loading &&
                                <Pagination
                                    data={paginationData}
                                    nextFunc={() => setPage(page + 1)}
                                    prevFunc={() => setPage(page - 1)}
                                    setPageRowFunc={(value) => {
                                        setPage(1);
                                        setPageSize(value);
                                    }}
                                />
                            }
                        </div>
                    </>
                )}

            </div >

            {/* Export Modal */}
            <Modal
                show={showExportModal}
                onHide={() => { setShowExportModal(false); setExportForm({ ...EMPTY_EXPORT_FORM }); }}
                centered
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <div className="d-flex align-items-center gap-3">
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: "#d1e7dd", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className="bi bi-file-earmark-arrow-down" style={{ fontSize: 20, color: "#0a3622" }} />
                        </div>
                        <div>
                            <Modal.Title className="fs_16 fw_600 mb-0">Export Activity Logs</Modal.Title>
                            <p className="text-muted fs_12 mb-0">Select a date range to download as CSV</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="pt-3 pb-2">
                    <div className="row g-3">
                        <div className="col-6">
                            <label className="form-label fs_12 fw_600 mb-1 text-uppercase text-secondary" style={{ letterSpacing: "0.04em" }}>From Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={exportForm.from_date}
                                max={exportForm.to_date || undefined}
                                onChange={(e) => setExportForm((prev) => ({ ...prev, from_date: e.target.value }))}
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label fs_12 fw_600 mb-1 text-uppercase text-secondary" style={{ letterSpacing: "0.04em" }}>To Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={exportForm.to_date}
                                min={exportForm.from_date || undefined}
                                onChange={(e) => setExportForm((prev) => ({ ...prev, to_date: e.target.value }))}
                            />
                        </div>
                    </div>
                    {exportForm.from_date && exportForm.to_date && (
                        <div className="mt-3 px-3 py-2 rounded d-flex align-items-center gap-2" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                            <i className="bi bi-calendar-range text-success" />
                            <span className="fs_13 text-success fw_500">
                                {moment(exportForm.from_date).format("MMM D, YYYY")}
                                <span className="text-muted fw_400 mx-1">to</span>
                                {moment(exportForm.to_date).format("MMM D, YYYY")}
                            </span>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-1 gap-2">
                    <button
                        className="btn btn-outline-secondary flex-fill"
                        onClick={() => { setShowExportModal(false); setExportForm({ ...EMPTY_EXPORT_FORM }); }}
                        disabled={exportLoading}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-success flex-fill d-flex align-items-center justify-content-center gap-2"
                        onClick={handleExportSubmit}
                        disabled={exportLoading || !exportForm.from_date || !exportForm.to_date}
                    >
                        {exportLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Exporting...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-download" />
                                Export CSV
                            </>
                        )}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Detail Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title className="fs_16 fw_600">Log Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedLog && (
                        <div>
                            <table className="table table-sm table-bordered log-detail-table mb-4">
                                <tbody>
                                    <tr>
                                        <th>ID</th>
                                        <td><code className="fs_12">{selectedLog.id}</code></td>
                                    </tr>
                                    <tr>
                                        <th>Timestamp</th>
                                        <td>{moment(selectedLog.createdAt).format("MMM D, YYYY h:mm:ss A")}</td>
                                    </tr>
                                    <tr>
                                        <th>Category</th>
                                        <td><CategoryBadge category={selectedLog.event_category} /></td>
                                    </tr>
                                    <tr>
                                        <th>Action</th>
                                        <td>{formatAction(selectedLog.event_action)}</td>
                                    </tr>
                                    <tr>
                                        <th>Status</th>
                                        <td><StatusBadge status={selectedLog.event_status} /></td>
                                    </tr>
                                    <tr>
                                        <th>Actor Email</th>
                                        <td>{selectedLog.actor_email || "—"}</td>
                                    </tr>
                                    <tr>
                                        <th>Actor Type</th>
                                        <td>{selectedLog.actor_type || "—"}</td>
                                    </tr>
                                    <tr>
                                        <th>Actor ID</th>
                                        <td><code className="fs_12">{selectedLog.actor_id || "—"}</code></td>
                                    </tr>
                                    <tr>
                                        <th>Company ID</th>
                                        <td><code className="fs_12">{selectedLog.company_id || "—"}</code></td>
                                    </tr>
                                    <tr>
                                        <th>Target Type</th>
                                        <td>{selectedLog.target_type || "—"}</td>
                                    </tr>
                                    <tr>
                                        <th>Target ID</th>
                                        <td><code className="fs_12">{selectedLog.target_id || "—"}</code></td>
                                    </tr>
                                    <tr>
                                        <th>IP Address</th>
                                        <td>{selectedLog.ip_address || "—"}</td>
                                    </tr>
                                    <tr>
                                        <th>User Agent</th>
                                        <td><small className="text-muted">{selectedLog.user_agent || "—"}</small></td>
                                    </tr>
                                    {selectedLog.event_status === "FAILED" && (
                                        <tr>
                                            <th className="text-danger">Error</th>
                                            <td className="text-danger">{selectedLog.error_message || "—"}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>

                            <h6 className="fw_600 fs_14 mb-2">Metadata</h6>
                            {renderMetadata(selectedLog.metadata)}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div >
    )
}
